import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const tableLayoutSx = {
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 1.5,
    },
    /**
     * Clears the app bar.
     *
     * Only when the card carries its own toolbar: those screens used to open with a page header whose
     * top margin did this, and without it the card's heading sits directly under the notification bell
     * in the bar above. A screen still using a page header already has that margin and must not get a
     * second one.
     */
    rootWithToolbar: {
        mt: 2.5,
    },
    /**
     * The card, when it is nested inside another one.
     *
     * A relationship tab is already a card, so a table drawn as a second card inside it reads as a
     * sheet of paper dropped on a sheet of paper. Same rows, same header, same footer — no chrome.
     */
    cardFlush: {
        bgcolor: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column' as const,
    },
    card: {
        // Room above the card when a page turn brings it back into view.
        scrollMarginTop: theme.spacing(2),
        bgcolor: theme.palette.common.white,
        borderRadius: '14px',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        // Below a desktop every row is a card of its own, carrying this same edge and shadow. A panel
        // around them put a card inside a card - two borders and two shadows for one list - so here
        // the list sits directly on the page, the way it is drawn.
        [theme.breakpoints.down('md')]: {
            bgcolor: 'transparent',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'visible',
        },
    },
    tableWrapper: {
        flex: 1,
        minWidth: 0,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1.25,
        borderTop: '1px solid rgba(0, 0, 0, 0.05)',
        bgcolor: '#FAFBFD',
        flexWrap: 'wrap' as const,
        // With the panel gone there is nothing for a strip to belong to: the pager stands on the page
        // under the last card, with no rule and no tint of its own, and on the same edge - the row
        // counter starts where every card starts and the pager ends where every card ends.
        [theme.breakpoints.down('md')]: {
            px: 0,
            pt: 2,
            pb: 0,
            borderTop: 'none',
            bgcolor: 'transparent',
        },
    },
    // Same strip, no padding of its own: whatever sits inside is the strip, so the whole width of it
    // is the click target rather than the middle of it.
    footerFlush: {
        display: 'flex',
        borderTop: '1px solid rgba(0, 0, 0, 0.05)',
        bgcolor: '#FAFBFD',
    },
};

/**
 * A single action filling the footer, label centred.
 *
 * For the sections whose footer is one verb rather than a pager - "add a client", "add a file". Left
 * to itself the button sat hard against the left edge of a full-width strip, reading as a stray
 * control rather than as the row's own action.
 */
export const addRowSx: SxProps<Theme> = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(5 / 8),
    py: 1.25,
    borderRadius: 0,
    color: theme.palette.text.primary,
};
